import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Database, 
  Plus, 
  Search, 
  Filter, 
  RefreshCw, 
  Trash2, 
  Edit, 
  Eye,
  Activity,
  HardDrive,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { apiClient } from '@/services/api-client';

// Types
interface DatabaseRecord {
  id: string;
  name: string;
  description?: string;
  data?: Record<string, any>;
  created_at: string;
  updated_at?: string;
  status?: string;
}

interface DatabaseStatus {
  mongodb: {
    status: string;
    version: string;
    collections: number;
    documents: number;
    size: string;
  };
  redis: {
    status: string;
    version: string;
    keys: number;
    memory: string;
    hit_rate: number;
  };
  postgresql: {
    status: string;
    version: string;
    tables: number;
    size: string;
  };
}

interface Collection {
  name: string;
  count: number;
  size: string;
  indexes: number;
}

// Database Manager Component
export default function DatabaseManager() {
  // State management
  const [records, setRecords] = useState<DatabaseRecord[]>([]);
  const [databaseStatus, setDatabaseStatus] = useState<DatabaseStatus | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [totalRecords, setTotalRecords] = useState(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DatabaseRecord | null>(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    data: ''
  });

  // Load initial data
  useEffect(() => {
    loadDatabaseData();
  }, []);

  // Load database data
  const loadDatabaseData = async () => {
    try {
      setLoading(true);
      
      // Load records, status, and collections in parallel
      const [recordsResponse, statusResponse, collectionsResponse] = await Promise.allSettled([
        apiClient.get('/api/v1/database/list'),
        apiClient.get('/api/v1/database/status'),
        apiClient.get('/api/v1/database/collections')
      ]);

      // Handle records
      if (recordsResponse.status === 'fulfilled') {
        const response = recordsResponse.value as any;
        const data = response.data;
        setRecords(data.records || []);
        setTotalRecords(data.total || 0);
      }

      // Handle database status
      if (statusResponse.status === 'fulfilled') {
        const response = statusResponse.value as any;
        setDatabaseStatus(response.data);
      }

      // Handle collections
      if (collectionsResponse.status === 'fulfilled') {
        const response = collectionsResponse.value as any;
        setCollections(response.data || []);
      }

    } catch (error) {
      console.error('Failed to load database data:', error);
      alert('Failed to load database data');
    } finally {
      setLoading(false);
    }
  };

  // Create record
  const handleCreateRecord = async () => {
    try {
      const response = await apiClient.post('/api/v1/database/create', {
        name: formData.name,
        description: formData.description,
        data: formData.data ? JSON.parse(formData.data) : {}
      }) as any;

      if (response.success) {
        alert('Record created successfully');
        setIsCreateDialogOpen(false);
        setFormData({ name: '', description: '', data: '' });
        loadDatabaseData(); // Refresh data
      }
    } catch (error) {
      console.error('Failed to create record:', error);
      alert('Failed to create record');
    }
  };

  // Update record
  const handleUpdateRecord = async () => {
    if (!selectedRecord) return;

    try {
      const response = await apiClient.put('/api/v1/database/update', {
        name: formData.name,
        description: formData.description,
        data: formData.data ? JSON.parse(formData.data) : {}
      }) as any;

      if (response.success) {
        alert('Record updated successfully');
        setIsEditDialogOpen(false);
        setSelectedRecord(null);
        setFormData({ name: '', description: '', data: '' });
        loadDatabaseData(); // Refresh data
      }
    } catch (error) {
      console.error('Failed to update record:', error);
      alert('Failed to update record');
    }
  };

  // Delete record
  const handleDeleteRecord = async (recordId: string) => {
    if (!confirm('Are you sure you want to delete this record?')) return;

    try {
      const response = await apiClient.delete('/api/v1/database/delete') as any;
      
      if (response.success) {
        alert('Record deleted successfully');
        loadDatabaseData(); // Refresh data
      }
    } catch (error) {
      console.error('Failed to delete record:', error);
      alert('Failed to delete record');
    }
  };

  // Edit record
  const handleEditRecord = (record: DatabaseRecord) => {
    setSelectedRecord(record);
    setFormData({
      name: record.name,
      description: record.description || '',
      data: record.data ? JSON.stringify(record.data, null, 2) : ''
    });
    setIsEditDialogOpen(true);
  };

  // Filtered records
  const filteredRecords = records.filter(record => {
    const matchesSearch = record.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (record.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || record.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'inactive':
        return <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>;
      default:
        return <Badge variant="outline"><AlertCircle className="w-3 h-3 mr-1" />Unknown</Badge>;
    }
  };

  // Get database status badge
  const getDatabaseStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Connected</Badge>;
      case 'disconnected':
        return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" />Disconnected</Badge>;
      default:
        return <Badge variant="outline"><AlertCircle className="w-3 h-3 mr-1" />Unknown</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span>Loading database data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Database Manager</h1>
          <p className="text-muted-foreground">
            Manage database records, monitor status, and perform operations
          </p>
        </div>
        <Button onClick={loadDatabaseData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Database Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {databaseStatus && (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">MongoDB</CardTitle>
                {getDatabaseStatusBadge(databaseStatus.mongodb.status)}
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{databaseStatus.mongodb.collections}</div>
                <p className="text-xs text-muted-foreground">
                  {databaseStatus.mongodb.documents.toLocaleString()} documents
                </p>
                <p className="text-xs text-muted-foreground">
                  {databaseStatus.mongodb.size} • v{databaseStatus.mongodb.version}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Redis</CardTitle>
                {getDatabaseStatusBadge(databaseStatus.redis.status)}
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{databaseStatus.redis.keys.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  {databaseStatus.redis.memory} • {databaseStatus.redis.hit_rate}% hit rate
                </p>
                <p className="text-xs text-muted-foreground">
                  v{databaseStatus.redis.version}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">PostgreSQL</CardTitle>
                {getDatabaseStatusBadge(databaseStatus.postgresql.status)}
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{databaseStatus.postgresql.tables}</div>
                <p className="text-xs text-muted-foreground">
                  {databaseStatus.postgresql.size}
                </p>
                <p className="text-xs text-muted-foreground">
                  v{databaseStatus.postgresql.version}
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="records" className="space-y-4">
        <TabsList>
          <TabsTrigger value="records">Records ({totalRecords})</TabsTrigger>
          <TabsTrigger value="collections">Collections ({collections.length})</TabsTrigger>
          <TabsTrigger value="operations">Operations</TabsTrigger>
        </TabsList>

        {/* Records Tab */}
        <TabsContent value="records" className="space-y-4">
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Record
            </Button>
          </div>

          {/* Records Table */}
          <Card>
            <CardHeader>
              <CardTitle>Database Records</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-mono text-sm">{record.id}</TableCell>
                      <TableCell className="font-medium">{record.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {record.description || '-'}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(record.status || 'unknown')}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(record.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditRecord(record)}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteRecord(record.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Collections Tab */}
        <TabsContent value="collections" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Database Collections</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Count</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Indexes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {collections.map((collection) => (
                    <TableRow key={collection.name}>
                      <TableCell className="font-medium">{collection.name}</TableCell>
                      <TableCell>{collection.count.toLocaleString()}</TableCell>
                      <TableCell>{collection.size}</TableCell>
                      <TableCell>{collection.indexes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Activity className="w-5 h-5 mr-2" />
                  Database Operations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button className="w-full" variant="outline">
                  <HardDrive className="w-4 h-4 mr-2" />
                  Backup Database
                </Button>
                <Button className="w-full" variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Optimize Database
                </Button>
                <Button className="w-full" variant="outline">
                  <Clock className="w-4 h-4 mr-2" />
                  Schedule Maintenance
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Database className="w-5 h-5 mr-2" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button className="w-full" variant="outline">
                  <Eye className="w-4 h-4 mr-2" />
                  View Schema
                </Button>
                <Button className="w-full" variant="outline">
                  <Filter className="w-4 h-4 mr-2" />
                  Run Query
                </Button>
                <Button className="w-full" variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Cache
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Record Dialog */}
      {isCreateDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Create New Record</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter record name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data (JSON)</label>
                <Textarea
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  placeholder='{"key": "value"}'
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRecord}>Create</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Record Dialog */}
      {isEditDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Edit Record</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter record name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data (JSON)</label>
                <Textarea
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  placeholder='{"key": "value"}'
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateRecord}>Update</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 